import { useState } from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * FormInput - Premium input component for /apply flow
 * 
 * Features:
 * - Clear visual hierarchy
 * - Strong focus states
 * - Friendly validation
 * - Consistent spacing
 */
const FormInput = ({
  label,
  type = 'text',
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  helpText,
  required = false,
  disabled = false,
  prefix,
  suffix,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);

  const showError = error && hasBlurred;

  const handleBlur = (e) => {
    setIsFocused(false);
    setHasBlurred(true);
    if (onBlur) onBlur(e);
  };

  return (
    <div className="form-input-wrapper" style={{ marginBottom: '24px' }}>
      {/* Label */}
      {label && (
        <label style={{
          fontSize: '14px',
          fontWeight: 500,
          color: 'var(--lk-text-primary)',
          marginBottom: '8px',
          display: 'block',
        }}>
          {label}
          {required && <span style={{ color: 'var(--lk-danger-500)', marginLeft: '4px' }}>*</span>}
        </label>
      )}

      {/* Input Container */}
      <div style={{ position: 'relative' }}>
        {prefix && (
          <div style={{
            position: 'absolute',
            left: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#6c757d',
            fontSize: '16px',
            pointerEvents: 'none',
          }}>
            {prefix}
          </div>
        )}

        <input
          type={type}
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            width: '100%',
            fontSize: '16px',
            padding: '12px 16px',
            paddingLeft: prefix ? '40px' : '16px',
            paddingRight: suffix ? '40px' : '16px',
            borderRadius: 'var(--lk-radius-md)',
            border: showError 
              ? '2px solid var(--lk-danger-500)' 
              : isFocused 
                ? '2px solid var(--lk-primary-500)' 
                : '1px solid var(--lk-border-default)',
            backgroundColor: disabled ? 'var(--lk-gray-50)' : 'white',
            transition: 'all 0.15s ease',
            boxShadow: isFocused ? '0 0 0 3px var(--lk-primary-100)' : 'none',
            outline: 'none',
          }}
          {...props}
        />

        {suffix && (
          <div style={{
            position: 'absolute',
            right: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#6c757d',
            fontSize: '16px',
            pointerEvents: 'none',
          }}>
            {suffix}
          </div>
        )}
      </div>

      {/* Help Text or Error */}
      {showError ? (
        <div style={{
          fontSize: '13px',
          color: '#dc3545',
          marginTop: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          <AlertCircle size={14} />
          {error}
        </div>
      ) : helpText ? (
        <div style={{
          fontSize: '13px',
          color: '#6c757d',
          marginTop: '6px',
        }}>
          {helpText}
        </div>
      ) : null}
    </div>
  );
};

export default FormInput;
